import Player from '@/models/Player';
import Team from '@/models/Team';

/**
 * Универсальное описание стороны серии или командного матча.
 *
 * MatchSide — это нормализованный объект участника, который может представлять:
 * - одного игрока (`entityType = 'player'`)
 * - фиксированную команду (`entityType = 'team'`)
 * - временную команду турнира (`entityType = 'team-key'`)
 *
 * Задача MatchSide — дать единый интерфейс для UI и расчётов, чтобы выше по
 * стеку не приходилось различать "игрок это или команда" в каждом месте вручную.
 */
export default class MatchSide {
  /**
   * @param {Object} params
   * @param {'player'|'team'|'team-key'} [params.entityType='player'] - Тип стороны.
   * @param {string|null} [params.id=null] - Устойчивый идентификатор стороны.
   * @param {string} [params.name=''] - Имя стороны для UI.
   * @param {Player[]} [params.members=[]] - Состав стороны.
   * @param {number|null} [params.seed=null] - Посев/позиция в сетке.
   * @param {Object|null} [params.ratingInfo=null] - Дополнительные рейтинговые данные.
   * @param {Object} [params.meta={}] - Служебные данные.
   * @param {Player|Team|Object|null} [params.entity=null] - Базовая сущность стороны.
   */
  constructor({
    entityType = 'player',
    id = null,
    name = '',
    members = [],
    seed = null,
    ratingInfo = null,
    meta = {},
    entity = null,
  } = {}) {
    this.entityType = entityType;
    this.id = id;
    this.name = name;
    this.members = members;
    this.seed = seed;
    this.ratingInfo = ratingInfo;
    this.meta = meta ?? {};
    this.entity = entity;
  }

  static fromObject(side = {}) {
    const entityType = side.entityType ?? 'player';
    const entity = MatchSide.#normalizeEntity(entityType, side.entity);
    const members = Array.isArray(side.members)
      ? side.members.map((member) => member instanceof Player ? member : Player.fromObject(member))
      : [];

    return new MatchSide({
      entityType,
      id: side.id ?? entity?.id ?? null,
      name: side.name ?? entity?.name ?? '',
      members,
      seed: side.seed ?? null,
      ratingInfo: side.ratingInfo ?? null,
      meta: side.meta ?? {},
      entity,
    });
  }

  static #normalizeEntity(entityType, entity) {
    if (!entity) return null;
    if (entityType === 'team') {
      return entity instanceof Team ? entity : Team.fromObject(entity);
    }
    if (entityType === 'player') {
      return entity instanceof Player ? entity : Player.fromObject(entity);
    }
    return entity;
  }

  /**
   * Возвращает `true`, если сторона представляет одного игрока.
   * @returns {boolean}
   */
  isPlayerSide() {
    return this.entityType === 'player';
  }

  /**
   * Возвращает `true`, если сторона представляет команду.
   * @returns {boolean}
   */
  isTeamSide() {
    return this.entityType === 'team' || this.entityType === 'team-key';
  }
}
